// fx / poster light — ONE document-level sun for every effect that opts in
// (2026-07-19 light-shape-finish lane; owner: "investigating a global
// lightsource that will enforce a standard light direction for any object on
// the poster that makes use of it").
//
// STORAGE: `posterLight = { angle 0-360, elevation 20-70 }` lives on the
// WORKSPACE object's data bag (`workspace.data.posterLight` — the workspace
// is the Rect named 'clip'; `data` is in JSON_KEYS, so the light serializes,
// loads, and rides every history snapshot like any other design state).
// useCanvasEditor owns the write path (getPosterLight/setPosterLight — one
// history entry per commit, same no-self-save contract as setTextFx).
//
// OPT-IN: fx 'bevel' and fx 'extrude' params carry `lightMode:
// 'custom' | 'poster'` (default 'custom' — existing designs are untouched).
// When 'poster', the bevel's shading azimuth/altitude and the 3D rig's KEY
// light direction derive from the shared value (see fxBevel.js and
// lighting3d.js posterKeyDirection for the two mappings).
//
// RE-BAKE ORCHESTRATION: fxRender's seam hash appends the poster values for
// any stack that opts in (stepsUsePosterLight) — editing the light changes
// those objects' hashes, and the EXISTING debounced machinery re-renders/
// re-bakes them; custom-mode objects hash identically and never re-bake.
//
// This module is deliberately dependency-free (no fabric, no registry): the
// effects (fxBevel/fxExtrude), the seam (fxRender) and the facade
// (useCanvasEditor) all read the same sanitizer, and probes/workers can load
// it standalone.

/** Angle shares the emboss dial convention: 0 = light from the RIGHT, CCW
 * positive (90 = from the top). Elevation = degrees above the poster plane.
 * Defaults match the 2D bevel's stock light (angle 135, hidden altitude 52),
 * so switching an untouched design to Poster changes nothing until the sun
 * is actually moved. */
export const POSTER_LIGHT_DEFAULTS = { angle: 135, elevation: 52 };

const clampNum = (v, min, max, fb) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return fb;
  return Math.min(max, Math.max(min, n));
};

/** Clamp/default a raw posterLight value (absent → the defaults). Always
 * returns a fresh plain object safe to serialize. */
export function sanitizePosterLight(pl) {
  const src = pl && typeof pl === 'object' ? pl : {};
  return {
    angle: clampNum(src.angle, 0, 360, POSTER_LIGHT_DEFAULTS.angle),
    elevation: clampNum(src.elevation, 20, 70, POSTER_LIGHT_DEFAULTS.elevation),
  };
}

/**
 * The poster light governing an object: read off its canvas's WORKSPACE
 * ('clip' sits at the back — index 0 in practice, so the scan is O(1)).
 * Detached objects (no canvas yet — probes, export clones mid-build) get the
 * defaults, which keeps hashes and renders deterministic either way.
 */
export function getPosterLightFor(obj) {
  const c = obj && obj.canvas;
  let raw = null;
  if (c && typeof c.getObjects === 'function') {
    const objs = c.getObjects();
    for (let i = 0; i < objs.length; i++) {
      const o = objs[i];
      if (o && o.name === 'clip') {
        raw = o.data && o.data.posterLight;
        break;
      }
    }
  }
  return sanitizePosterLight(raw);
}

/** True when any step in a data.fx stack opts into the poster light. Reads
 * the RAW stored params (patchTextFx stores sanitized params, and a
 * hand-edited 'poster' on an unknown step merely adds hash salt). */
export function stepsUsePosterLight(steps) {
  if (!Array.isArray(steps)) return false;
  for (const step of steps) {
    if (step && step.params && step.params.lightMode === 'poster') return true;
  }
  return false;
}
